
import React, { useState, useEffect } from 'react';
import { useSignalData } from '../context/SignalContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle, Zap, Users, BookOpen, ShieldCheck, 
  Copy, X, ArrowRight, LogIn, Check, Loader2, Phone, Mail, User as UserIcon,
  MessageSquare, Layout, Lock, LucideIcon
} from 'lucide-react';

// === CẤU HÌNH (CONFIGURATION) ===
// Added explicit string type to avoid TypeScript error: "This comparison appears to be unintentional because the types... have no overlap"
const WEB_APP_URL: string = "https://script.google.com/macros/s/AKfycbz8-0Jv8Xe1q-z38aRQTygPCQPaucrDrQ-WvyPqEc1hmLf8QeX3W_N5h7g4FDgJsRmw/exec";
const LOGO_URL = "https://ui-avatars.com/api/?name=S&background=050B14&color=22d3ee"; 
const QR_CODE_URL = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=0x8ced252446b615a585f26ae855fddf1dcd5752b6";

interface BenefitItemProps {
  icon: LucideIcon;
  text: string;
  highlight?: boolean;
}

const BenefitItem: React.FC<BenefitItemProps> = ({ icon: Icon, text, highlight = false }) => (
  <div className={`flex items-start gap-3 ${highlight ? 'text-cyan-300 font-medium' : 'text-slate-200'}`}>
    <div className={`p-1.5 rounded-lg flex-shrink-0 ${highlight ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-400'}`}>
      <Icon className="w-5 h-5" />
    </div>
    <span className="text-sm md:text-base leading-relaxed">{text}</span>
  </div>
);

export const LandingPage: React.FC = () => {
  const { login, register, recordClick, currentUser } = useSignalData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // States
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [userIp, setUserIp] = useState<string>('...');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  
  const [formData, setFormData] = useState({ 
    fullName: '', 
    phone: '', 
    email: '', 
    telegramId: '', 
    tradingViewId: '', 
    referrerId: '' 
  });

  const [isRefLocked, setIsRefLocked] = useState(false);

  useEffect(() => {
    if (currentUser) navigate('/dashboard');
  }, [currentUser, navigate]);

  useEffect(() => {
    // Lấy IP người dùng
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setUserIp(data.ip))
      .catch(() => setUserIp('N/A'));

    // Xử lý mã giới thiệu
    const refCode = searchParams.get('ref') || localStorage.getItem('sva_ref_code');
    if (refCode) {
        localStorage.setItem('sva_ref_code', refCode);
        recordClick(refCode);
        setFormData(prev => ({...prev, referrerId: refCode}));
        setIsRefLocked(true);
    }
  }, [searchParams, recordClick]);

  const handleCopyAddress = () => {
    navigator.clipboard.writeText('0x8ced252446b615a585f26ae855fddf1dcd5752b6');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await login(loginData.email, loginData.password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        alert(result.message || 'Đăng nhập thất bại');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Có lỗi xảy ra khi đăng nhập. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceUpdateAdmin = async () => {
    if (window.confirm('Bạn có chắc muốn force update admin user không?')) {
      try {
        const { db } = await import('../lib/database');
        await db.forceUpdateAdmin();
        alert('✅ Đã cập nhật admin user thành công! Vui lòng thử đăng nhập lại.');
        setShowDebugInfo(false);
      } catch (error) {
        console.error('Force update error:', error);
        alert('❌ Có lỗi xảy ra khi cập nhật.');
      }
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // 1. Gửi lên Google Apps Script (Telegram/Sheet notification)
      // Check if WEB_APP_URL is provided to handle registration notifications
      if (WEB_APP_URL && WEB_APP_URL.length > 0) {
        try {
          await fetch(WEB_APP_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...formData,
              ipAddress: userIp,
              timestamp: new Date().toLocaleString('vi-VN')
            })
          });
        } catch (err) {
          console.warn("Lỗi gửi thông báo WebApp:", err);
        }
      }

      // 2. Đăng ký tài khoản chính thức trong hệ thống Firebase
      await register({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        telegramId: formData.telegramId,
        tradingViewId: formData.tradingViewId,
        referredBy: formData.referrerId
      });
      
      setShowSuccess(true);
    } catch (error) {
      console.error(error);
      alert("Đã có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050B14] text-slate-300 font-sans relative overflow-x-hidden">
       {/* Glow effects */}
       <div className="fixed top-[-10%] left-[-10%] w-[50%] h-[50%] bg-cyan-600/10 rounded-full blur-[120px] pointer-events-none"></div>
       <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none"></div>
       
       {/* Header */}
       <nav className="fixed top-0 w-full z-50 px-6 py-6 flex justify-between items-center bg-[#050B14]/40 backdrop-blur-md">
            <div className="flex items-center gap-3 group cursor-pointer" onClick={() => navigate('/')}>
                <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full blur opacity-50 group-hover:opacity-100 transition duration-200"></div>
                    <img src={LOGO_URL} className="relative w-10 h-10 rounded-full border border-white/10" alt="SignalViet" />
                </div>
                <span className="font-bold text-white text-xl tracking-tight">Signal<span className="text-cyan-400">Viet</span></span>
            </div>
            <button onClick={() => {
                setIsLoginModalOpen(true);
                setIsSubmitting(false);
                setLoginData({ email: '', password: '' });
            }} className="text-white font-semibold text-sm transition-all hover:text-cyan-400 px-4 py-2 rounded-lg hover:bg-white/5">
                Đăng nhập
            </button>
       </nav>

       {/* Main Content */}
       <main className="pt-32 pb-20 px-4 md:px-12 max-w-7xl mx-auto flex flex-col lg:grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Hero Section */}
            <div className="space-y-10 animate-fade-in-up">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
                    </span>
                    <span className="text-[12px] font-medium text-cyan-400">Ưu đãi giới hạn: Tặng thêm 2 tháng</span>
                </div>

                <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-[1.1] tracking-tight">
                    Hệ Thống Trading <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                        Tự Do & Bền Vững
                    </span>
                </h1>
                
                <p className="text-lg text-slate-400 leading-relaxed max-w-xl">
                    Sở hữu ngay bộ công cụ cảnh báo rủi ro, Bot tín hiệu chuẩn xác và tham gia cộng đồng VIP để kiếm tiền bền vững mà không cần canh chart 24/7.
                </p>

                <div className="space-y-4">
                    <BenefitItem icon={Zap} text="Bộ Chỉ Báo SignalViet độc quyền ($1,000)" />
                    <BenefitItem icon={Users} text="Tham gia nhóm Private & Bot Tín hiệu ($2,000)" />
                    <BenefitItem icon={BookOpen} text="Khóa học Trading Master A-Z ($2,000)" />
                    <BenefitItem icon={ShieldCheck} text="Hoàn tiền 100% trong 30 ngày nếu không hài lòng" highlight />
                </div>

                <div className="flex items-center gap-4 pt-6">
                    <div className="flex -space-x-3">
                        {[1, 2, 3, 4].map(i => (
                            <img key={i} src={`https://ui-avatars.com/api/?name=U${i}&background=random&color=fff`} className="w-10 h-10 rounded-full border-2 border-[#050B14]" alt="User" />
                        ))}
                    </div>
                    <span className="text-sm text-slate-500 font-medium">Hơn <span className="text-white font-bold">100+ Trader</span> đã tham gia</span>
                </div>
            </div>

            {/* Form Section */}
            <div className="w-full max-w-[500px] lg:justify-self-end">
                {showSuccess ? (
                    <div className="bg-[#0F1623]/80 backdrop-blur-xl border border-white/10 rounded-[32px] p-10 text-center shadow-2xl animate-fade-in">
                        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-green-400">
                            <CheckCircle className="w-10 h-10" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-4">Đăng ký thành công!</h3>
                        <p className="text-slate-400 mb-8 leading-relaxed">
                            Cảm ơn <span className="text-cyan-400 font-bold">{formData.fullName}</span>.<br/>
                            Hệ thống đang kiểm tra giao dịch. Link tham gia nhóm Private sẽ được gửi qua Telegram/Zalo của bạn trong giây lát.
                        </p>
                        <button onClick={() => navigate('/dashboard')} className="w-full bg-cyan-500 hover:bg-cyan-400 py-4 rounded-xl font-bold text-[#050B14] transition-all shadow-lg shadow-cyan-500/20">
                            Truy cập Dashboard
                        </button>
                    </div>
                ) : (
                    <div className="bg-[#0F1623]/80 backdrop-blur-xl border border-white/5 rounded-[32px] p-8 shadow-2xl">
                        <h2 className="text-2xl font-bold text-white text-center mb-1">Đăng Ký Tham Gia Ngay</h2>
                        <p className="text-slate-500 text-xs text-center mb-8 uppercase tracking-widest font-bold opacity-60">Nhận quyền truy cập vào nhóm Private</p>
                        
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-400 ml-1">Họ và tên</label>
                                <input 
                                    placeholder="Nguyễn Văn A" required 
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all placeholder:text-slate-700"
                                    value={formData.fullName}
                                    onChange={e => setFormData({...formData, fullName: e.target.value})}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-400 ml-1">Số điện thoại (Zalo)</label>
                                    <input 
                                        placeholder="0912..." required
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all placeholder:text-slate-700"
                                        value={formData.phone}
                                        onChange={e => setFormData({...formData, phone: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-400 ml-1">Telegram ID</label>
                                    <input 
                                        placeholder="@username" required
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all placeholder:text-slate-700"
                                        value={formData.telegramId}
                                        onChange={e => setFormData({...formData, telegramId: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-400 ml-1">Email nhận tài liệu</label>
                                    <input 
                                        type="email" placeholder="email@example.com" required
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all placeholder:text-slate-700"
                                        value={formData.email}
                                        onChange={e => setFormData({...formData, email: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-400 ml-1">ID Tradingview của bạn</label>
                                    <input 
                                        placeholder="username_tv" required
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all placeholder:text-slate-700"
                                        value={formData.tradingViewId}
                                        onChange={e => setFormData({...formData, tradingViewId: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-400 ml-1 flex items-center justify-between">
                                    <span>ID Tradingview người giới thiệu</span>
                                    {isRefLocked && (
                                      <span className="flex items-center gap-1 text-[10px] text-cyan-400 font-bold tracking-tight animate-pulse">
                                        <Lock size={10} className="mb-0.5"/> ĐÃ KHÓA THEO LINK REF
                                      </span>
                                    )}
                                </label>
                                <input 
                                    placeholder="username_tv_nguoi_gioi_thieu"
                                    readOnly={isRefLocked}
                                    className={`w-full bg-black/40 border rounded-xl px-4 py-3 text-white outline-none transition-all placeholder:text-slate-700 ${
                                      isRefLocked 
                                      ? 'opacity-70 cursor-not-allowed border-cyan-500/30 bg-cyan-500/5 font-bold' 
                                      : 'border-white/10 focus:ring-1 focus:ring-cyan-500/50'
                                    }`}
                                    value={formData.referrerId}
                                    onChange={e => !isRefLocked && setFormData({...formData, referrerId: e.target.value})}
                                />
                            </div>

                            {/* Payment Block */}
                            <div className="bg-black/30 rounded-2xl p-4 border border-cyan-500/10 space-y-4">
                                <div className="flex flex-col sm:flex-row gap-4 items-center">
                                    <div className="flex-1 space-y-3 w-full">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-medium text-slate-300">Gói Membership (1+2 Tháng)</span>
                                            <span className="text-lg font-bold text-cyan-400 uppercase tracking-tighter">100 USDT</span>
                                        </div>
                                        <div className="h-[1px] w-full bg-white/5"></div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest flex justify-between items-center">
                                                Mạng lưới: <span className="text-slate-300">BEP20</span>
                                            </p>
                                            <div className="pt-1">
                                                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest mb-1">Địa chỉ USDT:</p>
                                                <div className="flex items-center gap-2 bg-black/40 p-2 rounded-lg border border-white/5">
                                                    <code className="text-[10px] text-slate-400 font-mono break-all flex-1">0x8ced252446b615a585f26ae855fddf1dcd5752b6</code>
                                                    <button type="button" onClick={handleCopyAddress} className="text-cyan-400 hover:text-cyan-300 shrink-0">
                                                        {copied ? <Check size={14} /> : <Copy size={14} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white p-1 rounded-lg shrink-0">
                                        <img src={QR_CODE_URL} className="w-24 h-24 sm:w-28 sm:h-28" alt="Payment QR" />
                                        <p className="text-[8px] text-slate-900 font-black text-center mt-1 uppercase">Quét thanh toán</p>
                                    </div>
                                </div>
                            </div>

                            <button 
                                type="submit" disabled={isSubmitting}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 py-4 rounded-xl font-bold text-white shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2 group transition-all transform hover:scale-[1.01] active:scale-[0.98]"
                            >
                                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Xác Nhận Đã Thanh Toán <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
                            </button>
                            
                            <p className="text-center text-[10px] text-slate-500 font-medium opacity-60">
                                <ShieldCheck className="w-3 h-3 inline mr-1 text-cyan-500" />
                                Cam kết hoàn tiền 100% trong 30 ngày nếu không hài lòng.
                            </p>
                        </form>
                    </div>
                )}
            </div>
       </main>

       {/* Footer */}
       <footer className="py-12 px-6 border-t border-white/5 text-center">
            <p className="text-xs text-slate-600">&copy; 2026 SignalViet. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-4">
              <button 
                  onClick={() => setShowDebugInfo(!showDebugInfo)} 
                  className="text-[8px] text-slate-800 hover:text-slate-600 opacity-30 hover:opacity-100 transition-opacity"
              >
                  {showDebugInfo ? 'Hide' : 'Debug'}
              </button>
              <a 
                  href="/db" 
                  className="text-[8px] text-slate-800 hover:text-cyan-400 opacity-30 hover:opacity-100 transition-opacity"
              >
                  DB Test
              </a>
            </div>
            {showDebugInfo && (
                <div className="mt-4 p-4 bg-slate-900/50 border border-white/10 rounded-lg text-left max-w-md mx-auto">
                    <p className="text-xs text-slate-400 mb-2 font-mono">🔧 Debug Tools</p>
                    <button 
                        onClick={handleForceUpdateAdmin}
                        className="w-full bg-yellow-600 hover:bg-yellow-500 py-2 px-4 rounded text-xs font-bold transition-all"
                    >
                        Force Update Admin Password
                    </button>
                    <p className="text-[10px] text-slate-500 mt-2">
                        Sử dụng khi không đăng nhập được. Cập nhật password admin về: <code className="text-yellow-400">123@5678</code>
                    </p>
                </div>
            )}
       </footer>

       {/* Login Modal */}
       {isLoginModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
            <div className="relative w-full max-w-sm bg-[#0F1623] border border-white/10 rounded-[32px] p-8 shadow-2xl">
                <button onClick={() => setIsLoginModalOpen(false)} className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full"><X /></button>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white">Đăng Nhập</h3>
                  <p className="text-slate-500 text-sm mt-1">Truy cập hệ thống quản lý</p>
                </div>
                <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Email / SĐT</label>
                        <input 
                            name="email" value={loginData.email} onChange={e => setLoginData({...loginData, email: e.target.value})} 
                            placeholder="Email hoặc Số điện thoại" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50" 
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Mật khẩu</label>
                        <input 
                            name="password" type="password" value={loginData.password} onChange={e => setLoginData({...loginData, password: e.target.value})} 
                            placeholder="Mật khẩu" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:ring-1 focus:ring-cyan-500/50" 
                        />
                    </div>
                    <button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-cyan-600 hover:bg-cyan-500 py-4 rounded-xl font-bold text-white transition-all mt-4 shadow-lg shadow-cyan-600/20 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Đang đăng nhập...
                            </>
                        ) : (
                            'Đăng nhập ngay'
                        )}
                    </button>
                    <div className="mt-6 text-center text-[11px] space-y-2 pt-4 border-t border-white/5">
                        <p className="text-slate-500 font-medium uppercase tracking-tight">Cần hỗ trợ đăng nhập:</p>
                        <a href="https://t.me/+1XEc2KUAg402ZDFl" target="_blank" rel="noopener noreferrer" className="text-cyan-400 font-bold hover:underline block truncate">
                          t.me/+1XEc2KUAg402ZDFl
                        </a>
                    </div>
                </form>
            </div>
        </div>
       )}
    </div>
  );
};
